import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  createProjectVitalTask,
  listProjectVitalIssues,
} from '../services/projectVitalService';

type Props = {
  open: boolean;
  onClose: () => void;
  defaultIssueId?: string | null;
  defaultArea?: string | null;
};

const AREAS = [
  'Personal',
  'Profesional',
  'Finanzas',
  'Administración',
  'Salud y deporte',
  'Alimentación',
  'Formación',
  'Viajes y ocio',
  'Proyecto Vital',
  'Sin clasificar',
];

export default function ProjectVitalTaskDialog({
  open,
  onClose,
  defaultIssueId = null,
  defaultArea = null,
}: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [title, setTitle] = useState('');
  const [area, setArea] = useState('Sin clasificar');
  const [dueDate, setDueDate] = useState('');
  const [isImportant, setIsImportant] = useState(false);
  const [issueId, setIssueId] = useState('');

  const issuesQuery = useQuery({
    queryKey: ['project-vital', 'issues'],
    queryFn: listProjectVitalIssues,
    enabled: open,
  });

  const availableIssues = useMemo(
    () =>
      (issuesQuery.data ?? []).filter(
        (issue) => issue.status !== 'resuelto',
      ),
    [issuesQuery.data],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setTitle('');
    setArea(defaultArea || 'Sin clasificar');
    setDueDate('');
    setIsImportant(false);
    setIssueId(defaultIssueId || '');
  }, [open, defaultIssueId, defaultArea]);

  const mutation = useMutation({
    mutationFn: createProjectVitalTask,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'tasks'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'issues'],
        }),
      ]);

      enqueueSnackbar('Tarea creada.', {
        variant: 'success',
      });

      onClose();
    },
    onError: () => {
      enqueueSnackbar('No se pudo crear la tarea.', {
        variant: 'error',
      });
    },
  });

  const handleSubmit = () => {
    const normalizedTitle = title.trim();

    if (!normalizedTitle) {
      return;
    }

    mutation.mutate({
      title: normalizedTitle,
      area,
      status: 'pendiente',
      due_date: dueDate || null,
      is_important: isImportant,
      issue_id: issueId || null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: '#111c2f',
          border: fullScreen
            ? 'none'
            : '1px solid rgba(148,163,184,0.16)',
          borderRadius: fullScreen ? 0 : 3.5,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 850 }}>
        Nueva tarea
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.2} sx={{ pt: 1.5 }}>
          <TextField
            label="Título"
            autoFocus
            required
            fullWidth
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={mutation.isPending}
          />

          <FormControl fullWidth>
            <InputLabel>Área</InputLabel>
            <Select
              label="Área"
              value={area}
              onChange={(event) => setArea(event.target.value)}
              disabled={mutation.isPending}
            >
              {AREAS.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Asunto</InputLabel>
            <Select
              label="Asunto"
              value={issueId}
              onChange={(event) => setIssueId(event.target.value)}
              disabled={mutation.isPending}
            >
              <MenuItem value="">
                Sin asunto
              </MenuItem>

              {availableIssues.map((issue) => (
                <MenuItem key={issue.id} value={issue.id}>
                  {issue.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Fecha límite"
            type="date"
            fullWidth
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
            disabled={mutation.isPending}
            InputLabelProps={{ shrink: true }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isImportant}
                onChange={(event) =>
                  setIsImportant(event.target.checked)
                }
                disabled={mutation.isPending}
              />
            }
            label="Importante"
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: fullScreen
            ? 'calc(20px + env(safe-area-inset-bottom))'
            : 2.5,
        }}
      >
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={mutation.isPending || !title.trim()}
        >
          {mutation.isPending
            ? 'Guardando...'
            : 'Crear tarea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
